import React, { useState } from 'react';
import { Link, withRouter } from 'react-router-dom';
import axios from 'axios';
import { useGoogleLogin } from '@react-oauth/google';
import LoginButton from '../components/login/LoginButton';
import { api } from '../shared/api/api';
import { authStore } from '../shared/security/AuthStore';
import { notificationStore } from '../components/layout/notifications/NotificationStore';
import { dialogStore } from '../components/layout/modals/DialogStore';
import AuthPageLayout from './AuthPageLayout';
import {
	finishReferSignIn,
	getReferIntentJobId
} from 'ats/pages/open-jobs/referralStorage';

function getLinkState(history) {
	return history.location.state?.apply
		? { apply: history.location.state.apply }
		: null;
}

function SignInPanel({ history }) {
	const linkState = getLinkState(history);
	const isReferring = Boolean(getReferIntentJobId(history.location.search));

	return (
		<>
			<div className="auth-page__copy">
				{isReferring ? (
					<p className="auth-page__copy-title">Sign in to refer a candidate</p>
				) : (
					<>
						<p className="auth-page__copy-title">Sign in to Mira with Google</p>
						<p className="auth-page__copy-subtitle">
							Use the same email linked to your account.
						</p>
					</>
				)}
			</div>

			<LoginButton
				render={({ onClick }) => (
					<button
						type="button"
						className="auth-page__google-button"
						onClick={onClick}>
						<img src="/auth/google-logo.svg" alt="" />
						<span>Continue with Google</span>
					</button>
				)}
			/>

			<p className="auth-page__footer">
				Don&apos;t have an account yet?{' '}
				<Link
					to={{
						pathname: '/signup',
						search: history.location.search,
						state: linkState
					}}>
					Sign Up
				</Link>
			</p>
		</>
	);
}

function SignUpPanel({ history }) {
	const [termsAccepted, setTermsAccepted] = useState(false);
	const linkState = getLinkState(history);

	const signUpUser = async (email, googleId, imageUrl, name, inviteToken) => {
		const data = await api.post('signup', {
			email: email,
			googleID: googleId,
			imageUrl: imageUrl,
			name: name,
			inviteToken
		});
		if (data.token && data.profile) {
			authStore.setToken(data.token, data.profile);
		}
		const referJobId = getReferIntentJobId(history.location.search);
		if (data.status === 'fail') {
			notificationStore.pushNotification({
				type: notificationStore.ERROR_NOTIFICATION,
				title: 'Sign Up Error',
				message:
					'There was a problem attempting to sign up. Please contact an administrator.'
			});
		} else if (data.status === 'deactivated-user') {
			dialogStore.openDialog({
				title: 'Your account is inactive',
				message: (
					<>
						Your Prediktive account was deactivated. If you want to reactivate
						your account contact us at{' '}
						<strong>
							<a href="mailto:talent.candidates@prediktive.com">
								talent.candidates@prediktive.com
							</a>
						</strong>
					</>
				),
				confirmLabel: 'Close',
				confirmButtonClass: 'primary-v2',
				hideCancelButton: true
			});
		} else if (referJobId) {
			finishReferSignIn(history, referJobId);
		} else if (history.location.state?.apply) {
			history.push('/candidate-portal/wizard', {
				apply: history.location.state.apply
			});
		} else {
			history.push('/');
		}
	};

	const handleSignupSuccess = async tokenResponse => {
		const { data: userInfo } = await axios.get(
			'https://www.googleapis.com/oauth2/v3/userinfo',
			{
				headers: { Authorization: `Bearer ${tokenResponse.access_token}` }
			}
		);

		const { email, sub: googleId, picture: imageUrl, name } = userInfo;
		const inviteToken = new URLSearchParams(history.location.search).get(
			'token'
		);

		signUpUser(email, googleId, imageUrl, name, inviteToken);
	};

	const handleSignupFailure = error => {
		const errorMessages = {
			idpiframe_initialization_failed: [
				"Couldn't initialize Authentication API",
				'Please check if you have cookies enabled'
			],
			popup_closed: [
				'Authentication aborted',
				'It seems you accidentally closed the popup before the authentication was done, please try again.'
			],
			access_denied: [
				'Insufficient permissions',
				'Please accept the minimum required permissions'
			],
			immediate_failed: null
		};

		const errorMessage = errorMessages[error.error || error.type];

		if (errorMessage) {
			notificationStore.pushErrorNotification(...errorMessage);
		}
	};

	const signup = useGoogleLogin({
		onSuccess: handleSignupSuccess,
		onError: handleSignupFailure,
		onNonOAuthError: handleSignupFailure,
		flow: 'implicit'
	});

	return (
		<>
			<div className="auth-page__copy">
				<p className="auth-page__copy-title">Create your account with Google</p>
				<p className="auth-page__copy-subtitle">
					It only takes a few minutes. We’ll guide you through the process.
				</p>
			</div>

			<div className="auth-page__terms">
				<label>
					<input
						type="checkbox"
						checked={!!termsAccepted}
						onChange={() => setTermsAccepted(!termsAccepted)}
					/>
					<span>
						I agree to the{' '}
						<a
							href="https://prediktive.com/terms"
							target="_blank"
							rel="noopener noreferrer">
							Terms Of Service
						</a>
						,{' '}
						<a
							href="https://prediktive.com/privacy"
							target="_blank"
							rel="noopener noreferrer">
							Privacy Policy
						</a>{' '}
						and{' '}
						<a
							href="https://prediktive.com/cookie_policy"
							target="_blank"
							rel="noopener noreferrer">
							Cookie Policy
						</a>
					</span>
				</label>
			</div>

			<button
				type="button"
				className="auth-page__google-button"
				disabled={!termsAccepted}
				onClick={() => signup()}>
				<img src="/auth/google-logo.svg" alt="" />
				<span>Continue with Google</span>
			</button>

			<p className="auth-page__footer">
				Already have an account?{' '}
				<Link
					to={{
						pathname: '/login',
						search: history.location.search,
						state: linkState
					}}>
					Sign In
				</Link>
			</p>
		</>
	);
}

function AuthPage({ history, location }) {
	const mode = location.pathname.startsWith('/signup') ? 'signup' : 'signin';

	return (
		<AuthPageLayout mode={mode}>
			<div key={mode} className="auth-page__switch">
				{mode === 'signin' ? (
					<SignInPanel history={history} />
				) : (
					<SignUpPanel history={history} />
				)}
			</div>
		</AuthPageLayout>
	);
}

export default withRouter(AuthPage);
export { AuthPage };
